/**
 * Confirm Utility
 * 
 * Custom branded confirmation modal using the existing Modal component.
 * Returns a Promise that resolves to true (OK) or false (Cancel).
 * 
 * @package PresszoneComments
 */

import { el, __ } from './dom.js';
import Modal from '../components/Modal.js';
import Button, { CancelButton } from '../components/Button.js';

/**
 * Show a custom confirmation dialog.
 * 
 * @param {string} message - The message to display
 * @param {Object} options - Configuration options
 * @param {string} [options.title] - Modal title
 * @param {string} [options.okLabel] - Label for the OK button
 * @param {string} [options.cancelLabel] - Label for the Cancel button
 * @param {string} [options.okVariant='primary'] - Variant for OK button
 * @param {string} [options.icon='❓'] - Title icon
 * @returns {Promise<boolean>}
 */
export function Confirm(message, options = {}) {
    const {
        title = __('Confirm Action', 'comments-press-zone'),
        okLabel = __('OK', 'comments-press-zone'),
        _cancelLabel = __('Cancel', 'comments-press-zone'),
        okVariant = 'primary',
        icon = '❓'
    } = options;

    return new Promise((resolve) => {
        const modal = new Modal({
            title,
            width: '440px',
            premium: true,
            icon,
            extraClass: 'presszone-comments-modal--confirm',
            onClose: () => resolve(false)
        });

        const content = el('div', { class: 'presszone-comments-confirm-content' },
            el('p', { style: 'font-size: 16px; margin: 0; line-height: 1.5; color: var(--presszone-comments-text);' }, message)
        );

        const actions = [
            CancelButton(() => {
                modal.close();
                resolve(false);
            }),
            Button({
                label: okLabel,
                variant: okVariant,
                onClick: () => {
                    modal.close();
                    resolve(true);
                }
            })
        ];

        modal.render(content, actions);
        modal.open();
    });
}

export default Confirm;
